'use client';
import { motion } from 'motion/react';
import React from 'react';

import { AnimatedBlob } from '@/components/AnimatedBlob';
import { ImageMedia } from '@/components/ImageMedia';
import { VideoMedia } from '@/components/VideoMedia';
import type { Hero, Media } from '@/payload-types';
import { cn } from '@/utils/cn';

import { HeroContent } from '../HeroContent';
import styles from './LargeHero.module.scss';

type Props = Hero & {
  locale?: string;
  children?: React.ReactNode;
  event?: boolean;
};

// Variants for the fade-up animation
const fadeUpVariant = (dependsOn: boolean) => ({
  hidden: { opacity: 0, y: 20 },
  visible: dependsOn ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 },
});

export const LargeHero: React.FC<Props> = (props) => {
  const {
    alignment,
    aspectRatio,
    backgroundColor,
    event = false,
    image,
    mediaType,
    showBlob,
    videoEmbedUrl,
    videoIsBackground,
    videoLoops,
    videoPoster,
  } = props;

  const MediaComponent =
    mediaType === 'image' && image ? (
      <ImageMedia
        className={styles.mediaImage}
        aspectRatio={aspectRatio}
        priority
        image={image}
        responsiveWidths={
          aspectRatio === 'square'
            ? {
                mobile: 767,
                desktop: 512,
                largeDesktop: 2256,
              }
            : aspectRatio === 'portrait'
              ? {
                  mobile: 767,
                  desktop: 512,
                  largeDesktop: 800,
                }
              : {
                  mobile: 767,
                  desktop: 1352,
                  largeDesktop: 2256,
                }
        }
      />
    ) : mediaType === 'video' && videoEmbedUrl ? (
      <VideoMedia
        aspectRatio={aspectRatio}
        videoPoster={videoPoster as Media}
        videoEmbedUrl={videoEmbedUrl}
        videoIsBackground={videoIsBackground as boolean}
        videoLoops={videoLoops as boolean}
      />
    ) : null;

  return (
    <div
      className={cn(
        styles.wrapper,
        backgroundColor ? styles[`wrapper--${backgroundColor}`] : ''
      )}
      data-hero
      data-background-color={backgroundColor || 'white'}
    >
      <div
        className={cn(
          'container',
          styles.container,
          MediaComponent && aspectRatio && styles.hasMedia,
          event && styles.event,
          alignment ? styles[alignment] : ''
        )}
      >
        <HeroContent {...props} />
        <motion.div
          className={cn(
            styles.media,
            alignment && styles[alignment],
            aspectRatio ? styles[aspectRatio] : ''
          )}
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true }}
          variants={fadeUpVariant(true)}
          style={{ willChange: 'opacity, transform' }}
        >
          {MediaComponent}
        </motion.div>
      </div>
      <div className={styles.animatedBlobWrapper}>
        {showBlob && <AnimatedBlob />}
      </div>
    </div>
  );
};
